/**
 * "Check these web facts": on a refresh draft (Improve this page), each sentence in the draft that only
 * the web supports (no source from the archive or the live page backs it), and each claim the fact check
 * found no support for but could not find in the text to cut. The editor checks each one (against its
 * links, or in the draft), edits or removes it if it is wrong, and ticks it off. Save and publish waits
 * for every tick. The panel also says how many claims the fact check cut.
 */
import { useMutation } from '@tanstack/react-query'
import { safeHref } from '../../lib/html'
import { describeError } from '../../lib/describeError'
import { setWebCheck, type PieceDetail, type WebPassage } from '../api'
import { canPublishRefresh } from '../refreshPages'
import { errorText, focus } from './ui'

type Props = { detail: PieceDetail; onDetail: (d: PieceDetail) => void; open: boolean }

const WEB_FACTS_COPY = 'These sentences are in the draft and only the web supports them. Check each against its link, then tick it.'
const UNLOCATED_COPY = 'The fact check found no support for these but could not find them in the text to cut. Find each in the draft, fix or remove it, then tick it.'

function cutLine(n: number): string {
  return n === 1 ? '1 claim was cut: the fact check found no support for it.' : `${n} claims were cut: the fact check found no support for them.`
}

export function WebChecks({ detail: d, onDetail, open }: Props) {
  const tick = useMutation({
    mutationFn: (v: { key: string; checked: boolean }) => setWebCheck(d.id, v.key, v.checked),
    onSuccess: (r) => { if (d.refresh) onDetail({ ...d, refresh: { ...d.refresh, web_passages: r.web_passages } }) },
  })
  const passages = d.refresh?.web_passages ?? []
  const cut = d.refresh?.cut ?? 0
  if (passages.length === 0 && cut === 0) return null
  const web = passages.filter((p) => p.kind !== 'unlocated')
  const unlocated = passages.filter((p) => p.kind === 'unlocated')
  const left = passages.filter((p) => !p.checked_by).length
  const status = passages.length === 0 ? '' : canPublishRefresh(d.refresh) ? ' All are ticked.' : ` ${left} left.`

  const item = (p: WebPassage) => {
    const id = `web-check-${d.id}-${p.key}`
    return (
      <li key={p.key} className="flex items-start gap-3">
        <input id={id} type="checkbox" className={`mt-1 h-5 w-5 flex-none ${focus}`} checked={!!p.checked_by}
               disabled={!open || tick.isPending} onChange={(e) => tick.mutate({ key: p.key, checked: e.target.checked })} />
        <div className="flex min-w-0 flex-col gap-1">
          {/* The claim and its links come from the run: shown as text and plain links only. */}
          <label htmlFor={id} className="break-words text-[14px] text-paper">{p.text}</label>
          {(p.urls ?? []).length > 0 && (
            <span className="flex flex-wrap gap-x-3 gap-y-1 text-[13px]">
              {(p.urls ?? []).map((u) => {
                const href = safeHref(u)
                return href
                  ? <a key={u} href={href} target="_blank" rel="noopener noreferrer" className={`break-all text-amber-deep underline underline-offset-2 hover:text-paper ${focus}`}>{u}</a>
                  : <span key={u} className="break-all text-paper-faint">{u}</span>
              })}
            </span>
          )}
          {p.checked_by && <span className="text-[12px] text-paper-faint">Ticked off by {p.checked_by}</span>}
        </div>
      </li>
    )
  }

  return (
    <section aria-label="Check these web facts" className="flex flex-col gap-3 rounded-xl border border-ink-500 bg-ink-700 px-[18px] py-4 font-ui">
      <h2 className="m-0 font-serif text-[20px] font-medium text-paper">Check these web facts</h2>
      {cut > 0 && <p className="m-0 text-[13px] text-paper-dim">{cutLine(cut)}</p>}
      {web.length > 0 && (
        <>
          <p className="m-0 text-[13px] text-paper-dim">{WEB_FACTS_COPY}{unlocated.length === 0 && status}</p>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">{web.map(item)}</ul>
        </>
      )}
      {unlocated.length > 0 && (
        <>
          <h3 className="m-0 text-[14px] font-semibold text-paper">Claims the fact check could not find to cut</h3>
          <p className="m-0 text-[13px] text-paper-dim">{UNLOCATED_COPY}{status}</p>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">{unlocated.map(item)}</ul>
        </>
      )}
      {tick.isError && <p role="alert" className={errorText}>{describeError(tick.error)}</p>}
    </section>
  )
}
